import type { VerifiedMemberDetail } from "@/api/giftcard.api";
import { memberProfile as defaultProfile, memberStats as defaultStats } from "./mockData";
import type { MemberProfile, MemberStat } from "./types";
import { getFormattedDateTime } from "@/utils";

export interface MappedVerifiedMember {
  profile: MemberProfile;
  stats: MemberStat[];
}

function getAvatarInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "M";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatDisplayDate(value?: string, fallback = "") {
  if (!value) return fallback;
  return getFormattedDateTime(value) || value;
}

/** Format as plain digits only — strips any currency symbol from API/mock values. */
function formatAmount(value: number | string | undefined) {
  if (value === undefined || value === null || value === "") return "0";

  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^\d.-]/g, ""));

  if (Number.isNaN(numeric)) return "0";
  return numeric.toLocaleString("en-IN");
}

/** Empty string means tier is missing from the API. */
export const MISSING_TIER = "";

/** True when tier should render as an unavailable icon instead of text. */
export function isMissingTier(value?: string | null) {
  if (value == null) return true;

  const cleaned = String(value)
    .replace(/\s*Member\s*$/i, "")
    .trim();

  return (
    !cleaned ||
    cleaned === "—" ||
    cleaned === "-" ||
    cleaned === "–" ||
    cleaned.toLowerCase() === "null" ||
    cleaned.toLowerCase() === "undefined" ||
    cleaned.toLowerCase() === "not assigned"
  );
}

/** Display-ready tier label — empty string when tier is missing. */
export function formatTierLabel(value?: string | null) {
  if (isMissingTier(value)) return MISSING_TIER;

  return String(value)
    .replace(/\s*Member\s*$/i, "")
    .trim();
}

/**
 * Map member detail (VerifyOTP `memberDetails[0]` or GetMemberDetails `newMemberDetails[0]`)
 * onto dashboard profile + stats cards.
 */
export function mapVerifiedMemberDetails(
  detail: VerifiedMemberDetail | undefined,
  mobileNo: string,
  existing?: Partial<MemberProfile>
): MappedVerifiedMember {
  const base = { ...defaultProfile, ...existing };
  const name = detail?.name?.trim() || base.name;
  // Prefer API tier when the key is present (including null); otherwise keep existing/mapped value.
  const tier =
    detail != null && "tier" in detail
      ? formatTierLabel(detail.tier)
      : formatTierLabel(base.tier);

  const profile: MemberProfile = {
    ...base,
    name,
    mobile: mobileNo || base.mobile,
    dob: formatDisplayDate(detail?.dob, base.dob),
    doa: formatDisplayDate(detail?.doa, base.doa),
    gender: detail?.gender?.trim() || base.gender,
    city: detail?.city?.trim() || base.city,
    tier,
    avatarInitials: getAvatarInitials(name),
  };

  const stats: MemberStat[] = defaultStats.map((stat) => {
    if (stat.id === "transactions") {
      return {
        ...stat,
        value: formatAmount(detail?.totalTxn),
      };
    }
    if (stat.id === "spent") {
      return {
        ...stat,
        value: formatAmount(detail?.spend),
      };
    }
    if (stat.id === "tier") {
      return {
        ...stat,
        value: tier,
      };
    }
    if (stat.id === "points") {
      return {
        ...stat,
        value: formatAmount(detail?.pointsBalance),
      };
    }
    return stat;
  });

  return { profile, stats };
}
